import { useCallback, useState } from 'react'

import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import DeleteForeverModal from 'modules/documents/components/TrashBin/TrashBinDataTable/_bulk_actions/DeleteForeverModal'
import RestoreModal from 'modules/documents/components/TrashBin/TrashBinDataTable/_bulk_actions/RestoreModal'
import useRemoveFromTrashBinDocument from 'modules/documents/hooks/documents/useRemoveFromTrashBinDocument'
import useRestoreFromTrashBinDocument from 'modules/documents/hooks/documents/useRestoreFromTrashBinDocument'
import { canDeleteDocuments } from 'modules/documents/lib/Documents'

import type { TrashBinDocumentNode } from './types'

const ACTION_IDS = {
  RESTORE: 'restore',
  DELETE_FOREVER: 'delete_forever',
} as const

type ActionId = (typeof ACTION_IDS)[keyof typeof ACTION_IDS]

const isActionId = (value: string): value is ActionId => {
  const values: ReadonlyArray<string> = Object.values(ACTION_IDS)
  return values.includes(value)
}

type ItemStatus = { checked: boolean; item: TrashBinDocumentNode }

export function useBulkActions() {
  const { restoreDocumentsFromTrashBin, loading: loadingRestore } = useRestoreFromTrashBinDocument({
    fromDataTable: true,
  })
  const { removeDocumentsFromTrashBin, loading: loadingRemove } = useRemoveFromTrashBinDocument({
    fromDataTable: true,
  })

  const [modal, setModal] = useState<ActionId | null>(null)
  const [selectedDocs, setSelectedDocs] = useState<TrashBinDocumentNode[]>([])

  const closeModal = () => setModal(null)

  const bulkActions = useCallback(({ itemsStatus }: { itemsStatus: readonly ItemStatus[] }) => {
    const selected = itemsStatus.filter((s) => s.checked).map((s) => s.item)
    if (selected.length === 0) return { primary: [], secondary: [] }

    const canDelete = canDeleteDocuments(selected)
    if (!canDelete) return { primary: [], secondary: [] }

    const primary: Array<{ id: ActionId; label: string }> = []
    const secondary: Array<{ id: ActionId; label: string }> = []

    primary.push({
      id: ACTION_IDS.RESTORE,
      label: i18n.t('documents.trash_bin.restore_files'),
    })

    secondary.push({
      id: ACTION_IDS.DELETE_FOREVER,
      label: i18n.t('documents.trash_bin.delete'),
    })

    return { primary, secondary }
  }, [])

  const handleBulkAction = useCallback(
    async (actionId: string, { itemsStatus }: { itemsStatus: readonly ItemStatus[] }) => {
      const selected = itemsStatus.filter((s) => s.checked).map((s) => s.item)
      setSelectedDocs(selected)

      if (isActionId(actionId)) {
        setModal(actionId)
      }
    },
    []
  )

  const onSubmitRestore = async () => {
    const ids = selectedDocs.map((d) => d.id)
    const result = await restoreDocumentsFromTrashBin({ documentIds: ids })
    const handled = handleResult(result, () => undefined)
    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.tp('documents.trash_bin.restore.success', { count: ids.length }),
      })
    } else {
      app.setFlash({ type: 'wrong', message: i18n.t('errors.generic_saving') })
    }
    closeModal()
  }

  const onSubmitDeleteForever = async () => {
    const ids = selectedDocs.map((d) => d.id)
    const result = await removeDocumentsFromTrashBin({ documentIds: ids })
    const handled = handleResult(result, () => undefined)
    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.tp('documents.trash_bin.delete_forever.success', { count: ids.length }),
      })
    } else {
      app.setFlash({ type: 'wrong', message: i18n.t('errors.generic_saving') })
    }
    closeModal()
  }

  const Modals = () => (
    <>
      {modal === ACTION_IDS.RESTORE && (
        <RestoreModal
          useF0
          filename={selectedDocs[0]?.filename ?? ''}
          totalDocuments={selectedDocs.length}
          loading={loadingRestore}
          onSubmit={onSubmitRestore}
          onClose={closeModal}
        />
      )}
      {modal === ACTION_IDS.DELETE_FOREVER && (
        <DeleteForeverModal
          useF0
          filename={selectedDocs[0]?.filename ?? ''}
          totalDocuments={selectedDocs.length}
          onClose={closeModal}
          onSubmit={onSubmitDeleteForever}
          loading={loadingRemove}
        />
      )}
    </>
  )

  return {
    bulkActions,
    handleBulkAction,
    Modals,
  }
}
